import { Menu } from "antd";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AboutModal } from "../../../../../app/features/About/components/AboutModal";
import { canAccess } from "../../../../../core/router/auth/permissions";
import { navigation } from "../../../../../core/router/config/navigation";
import { useActiveMenuContext } from "../../../../../core/router/navigation/activeMenuContext";
import { isMenuItemGroup } from "../../../../../core/router/types/menuTypes";
import { TextStyle } from "../../../../../core/styles/TextStyles";
import { CustomIcon } from "../../../../Icon/custom";
import { Link } from "../../../../Link";

const { SubMenu } = Menu;

/**
 * Properties of the Menu component
 * */
type MenuComponentProps = Readonly<{}>;

/**
 * MainMenu component is responsible for
 * building the menu from navigation.
 * Every item of the menu should point to the corresponding path.
 * */
export const MainMenu: FC<MenuComponentProps> = () => {
    const { t } = useTranslation();
    const { selectedMenuKey, openSubMenuKey } = useActiveMenuContext();
    const [openKeys, setOpenKeys] = useState<string[]>(openSubMenuKey ? [openSubMenuKey] : []);
    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

    useEffect(() => {
        if (!openSubMenuKey) {
            return;
        }

        setOpenKeys((currentOpenKeys) =>
            currentOpenKeys.includes(openSubMenuKey) ? currentOpenKeys : [...currentOpenKeys, openSubMenuKey],
        );
    }, [openSubMenuKey]);

    const selectedKeys = useMemo(() => (selectedMenuKey ? [selectedMenuKey] : []), [selectedMenuKey]);

    const handleAction = (action: string) => {
        if (action === "about") {
            setIsAboutModalOpen(true);
        }
    };

    return (
        <>
            <Menu selectedKeys={selectedKeys} openKeys={openKeys} mode="inline" onOpenChange={(keys) => setOpenKeys(keys)}>
                {navigation.map((item) => {
                    if (isMenuItemGroup(item)) {
                        const filteredSubMenus = item.subMenus
                            ?.map((subMenu) => ({
                                ...subMenu,
                                menuItems: subMenu.menuItems.filter(
                                    (menuItem) => !menuItem?.permission || canAccess(menuItem?.permission, "canRead"),
                                ),
                                actionItems: subMenu.actionItems?.filter(
                                    (actionItem) => !actionItem?.permission || canAccess(actionItem?.permission, "canRead"),
                                ),
                            }))
                            .filter((subMenu) => subMenu.menuItems.length > 0 || (subMenu.actionItems?.length ?? 0) > 0);

                        const filteredUnclassifiedItems = item.unclassifiedItems?.filter(
                            (menuItem) => !menuItem?.permission || canAccess(menuItem?.permission, "canRead"),
                        );

                        if (
                            (!filteredSubMenus || filteredSubMenus.length === 0) &&
                            (!filteredUnclassifiedItems || filteredUnclassifiedItems.length === 0)
                        ) {
                            return null;
                        }

                        return (
                            <Menu.ItemGroup key={`${item.key}-submenu`} title={t(item.title)}>
                                {filteredSubMenus?.map(({ key, icon, title, menuItems, actionItems }) => (
                                    <SubMenu
                                        key={key}
                                        icon={icon ? <CustomIcon iconSource={icon} /> : null}
                                        title={<span style={{ marginLeft: "10px" }}>{t(title)}</span>}
                                    >
                                        {menuItems.map(({ route, to, titleKey }) => (
                                            <Menu.Item key={to}>
                                                <Link route={route} to={to} className={item.key}>
                                                    {t(titleKey)}
                                                </Link>
                                            </Menu.Item>
                                        ))}
                                        {actionItems?.map(({ action, titleKey }) => (
                                            <Menu.Item key={`action-${action}`} onClick={() => handleAction(action)}>
                                                {t(titleKey)}
                                            </Menu.Item>
                                        ))}
                                    </SubMenu>
                                ))}
                                {filteredUnclassifiedItems?.map(({ to, menuIcon, route, titleKey }) => (
                                    <Menu.Item key={to} icon={menuIcon ? <CustomIcon iconSource={menuIcon} /> : null}>
                                        <Link route={route} to={to} className={item.key}>
                                            <span style={{ marginLeft: "10px" }}>{t(titleKey)}</span>
                                        </Link>
                                    </Menu.Item>
                                ))}
                            </Menu.ItemGroup>
                        );
                    }
                    return null;
                })}
            </Menu>
            <TextStyle style={{ textAlign: "center" }}>APAdmin v{APP_INFO.VERSION}</TextStyle>
            <AboutModal open={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
        </>
    );
};
